import { useCancelBooking } from "api/api-hooks/use-cancel-booking";
import { useReservation, useUpdateReservation } from "api/api-hooks/use-create-reservation";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import queryClient from "query-client";
import { useBookingId } from "hooks/use-booking-id";
import { useNavigate } from "react-router-dom";

const useRemoveActivity = (
    beforeRemoveLastCallback?: () => void,
    onRemoveCompleteCallback?: () => void
) => {
    const { setState } = useBookingActionsContext();
    const state = useBookingStateContext();
    const { data: reservation } = useReservation();
    const updateBooking = useUpdateReservation();
    const cancelBooking = useCancelBooking();
    const bookingId = useBookingId();
    const navigate = useNavigate();
    const { guests, guestsChildren, date } = state;

    const removeActivity = async (ruleId: number, sectionId: number) => {
        const startTime = reservation?.articles
            ?.filter((article) => article.ruleId === ruleId)
            ?.at(0)?.start;

        const times = state.times ? state.times.filter((time) => time.ruleId !== ruleId) : [];
        const stateWithTimes = {
            ...state,
            times,
        };
        setState?.(stateWithTimes);

        const activityToRemove =
            reservation?.getBookTimes()?.filter((time) => time.ruleId === ruleId) ?? [];

        const uniqueIds = [...new Set(reservation?.articles.map((item) => item.ruleId))];
        if (uniqueIds.length === 1) {
            // If this is the last item in the booking, we need to remove the query for the booking

            if (beforeRemoveLastCallback) beforeRemoveLastCallback();
            await cancelBooking.mutateAsync({});
            const pathParts = window.location.pathname.split("/").filter((part) => part);
            pathParts.pop();
            const newPath = pathParts.length > 0 ? `/${pathParts.join("/")}` : "/";

            queryClient.removeQueries({
                queryKey: ["booking", bookingId],
            });
            await navigate(
                {
                    pathname: newPath,
                    search: `?guests=${guests}&children=${guestsChildren}&date=${date?.format("YYYY-MM-DD") ?? ""}`,
                },
                { state: { intercept: false } }
            );
        } else if (activityToRemove.length > 0 && startTime) {
            await updateBooking.mutateAsync({
                state: stateWithTimes,
                timesToUpdate: [],
                removeTimes: [{ sectionId, startTime }],
            });
            if (onRemoveCompleteCallback) onRemoveCompleteCallback();
        }
    };
    return { removeActivity, isPendig: updateBooking.isPending || cancelBooking.isPending };
};

export { useRemoveActivity };
